import { useEffect, useState } from 'react';
import { useMe } from 'hooks/auth';
import { useUpdateMe } from 'hooks/user';
import { useRecordingObjectionStatus } from 'hooks/useRecordingObjectionStatus';
import { trackError } from 'utils/trackError';
import { RecordingDisclaimer } from './RecordingDisclaimer';
import { RecordingObjectionDialog } from './RecordingObjectionDialog';
import { RecordingObjectionNotice } from './RecordingObjectionNotice';

interface AudioRecordingConsentProps {
  medatixxPatientId: string | undefined;
  isContinuingWithoutRecording?: boolean;
}

export const AudioRecordingConsent = ({
  medatixxPatientId,
  isContinuingWithoutRecording = false,
}: AudioRecordingConsentProps) => {
  const { data: recordingObjection } = useRecordingObjectionStatus(medatixxPatientId);
  const activeObjection = recordingObjection?.active ? recordingObjection : undefined;
  const { data: user } = useMe();
  const { mutate: updateMe } = useUpdateMe();
  const [isDisclaimerExpanded, setIsDisclaimerExpanded] = useState(true);
  const [isObjectionDialogOpen, setIsObjectionDialogOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setIsDisclaimerExpanded(user.recordingDisclaimerExpanded);
    }
  }, [user]);

  const handleToggleDisclaimer = () => {
    const expanded = !isDisclaimerExpanded;
    setIsDisclaimerExpanded(expanded);
    updateMe(
      { recordingDisclaimerExpanded: expanded },
      {
        onError: (error) => trackError(error, { context: 'updateRecordingDisclaimerExpanded' }),
      }
    );
  };

  return (
    <>
      {isObjectionDialogOpen && (
        <RecordingObjectionDialog
          medatixxPatientId={medatixxPatientId}
          onClose={() => setIsObjectionDialogOpen(false)}
        />
      )}
      {activeObjection ? (
        <RecordingObjectionNotice
          objection={activeObjection}
          medatixxPatientId={medatixxPatientId}
          isContinuingWithoutRecording={isContinuingWithoutRecording}
        />
      ) : (
        <RecordingDisclaimer
          isExpanded={isDisclaimerExpanded}
          onToggleExpanded={handleToggleDisclaimer}
          onOpenCaptureObjectionModal={() => setIsObjectionDialogOpen(true)}
        />
      )}
    </>
  );
};
